import React from "react";
import { ActivityIndicator, View } from "react-native";
import { useQuery } from "@apollo/client";
import { createStackNavigator } from "@react-navigation/stack";
import { useRoute } from "@react-navigation/native";
import { useDispatch } from "react-redux";

import PersonalInformationInputScreen from "../screens/PersonalInformationInputScreen";
import PhoneNumberInputScreen from "../screens/PhoneNumberInputScreen";
import NewServiceForVisitScreen from "../screens/NewServiceForVisitScreen";
import ServiceCategorySelectionForVisitScreen from "../screens/ServiceCategorySelectionForVisitScreen";
import ServiceSelectionForVisitScreen from "../screens/ServiceSelectionForVisitScreen";
import ReviewServicesForVisitScreen from "../screens/ReviewServicesForVisitScreen";
import MedicalHistoryForVisitScreen from "../screens/MedicalHistoryForVisitScreen";
import MedicalConcernsForVisitScreen from "../screens/MedicalConcernsForVisitScreen";
import VisitConfirmedScreen from "../screens/VisitConfirmedScreen";
import ReviewVisitScreen from "../screens/ReviewVisitScreen";
import SignatureCapture from "../screens/SignatureCapture";
import SignatureCaptureWeb from "../screens/SignatureCapture/SignatureCaptureWeb";
import IdVerificationScreen from "../screens/IdVerificationScreen";
import ConsentQuestions from "../screens/ConsentQuestions";
import { colors } from "../theme";
import ServiceDetailsEntryForVisitScreen from "../screens/ServiceDetailsEntryForVisitScreen/ServiceDetailsEntryForVisitScreen";
import { VISIT } from "../screens/VisitScreen/gql";
import ExistingVisitScreen from "../screens/ExistingVisitScreen/ExistingVisitScreen";
import { VisitService, VisitStatus } from "../types";
import { SelectedService, setServices } from "../state/consentFlow";
import { stepHeader } from "./NewVisitFlow";
import VisitSubjectScreen from "../screens/VisitSubjectScreen";
import MinorDateOfBirthInputScreen from "../screens/MinorDateOfBirthInputScreen";
import MinorInformationInputScreen from "../screens/MinorInformationInputScreen";
import MinorAddedScreen from "../screens/MinorAddedScreen";
import NewVisitScreen from "../screens/NewVisitScreen";
import DateOfBirthInputScreen from "../screens/DateOfBirthInputScreen";

function selectedServicesFromVisit(
  services: VisitService[] = [],
): SelectedService[] {
  return services.map((visitService) => ({
    isExistingService: true,
    id: visitService.id,
    serviceId: visitService.service.id,
    serviceType: visitService.service.serviceType?.type,
    submittedInformation: (visitService.submittedInformation || []).map(
      (information) => ({
        name: information.name,
        label: information.label,
        value: information.value || "",
      }),
    ),
  }));
}

const Stack = createStackNavigator();

const ExistingVisitFlow = () => {
  const { params } = useRoute() as any;
  const dispatch = useDispatch();
  const { loading, data } = useQuery(VISIT, {
    variables: { visitId: params.visitId },
  });

  React.useEffect(() => {
    if (data?.visit?.services) {
      dispatch(setServices(selectedServicesFromVisit(data.visit.services)));
    }
  }, [data?.visit?.id, data?.visit?.services, dispatch]);

  if (loading && !data) {
    return (
      <View
        style={{
          flex: 1,
          backgroundColor: colors.black,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ActivityIndicator size="large" color={colors.yellow} />
      </View>
    );
  }

  const { visit } = data || {};
  const { store } = visit;

  const shouldFillConsent = visit.status === VisitStatus.ConsentRequested;
  const totalSteps = shouldFillConsent ? 5 : 3;

  return (
    <Stack.Navigator screenOptions={{ headerTitleAlign: "center" }}>
      <Stack.Screen
        name="NewVisit"
        component={NewVisitScreen}
        options={{ headerShown: false }}
        initialParams={{ store, visit, nextScreen: null }}
      />
      <Stack.Screen
        name="DateOfBirthInput"
        component={DateOfBirthInputScreen}
        options={stepHeader({
          title: "Basic Info",
          totalSteps,
          position: 1,
        })}
        initialParams={{ store, visit, nextScreen: null }}
      />
      <Stack.Screen
        name="VisitSubject"
        component={VisitSubjectScreen}
        options={stepHeader({
          title: "Visit Information",
          totalSteps,
          position: 1,
        })}
        initialParams={{ store, visit, nextScreen: "ExistingVisit" }}
      />
      <Stack.Screen
        name="MinorDateOfBirth"
        component={MinorDateOfBirthInputScreen}
        options={stepHeader({
          title: "Minor's Basic Info",
          totalSteps,
          position: 1,
        })}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="MinorDetails"
        component={MinorInformationInputScreen}
        options={stepHeader({
          title: "Minor's Basic Info",
          totalSteps,
          position: 1,
        })}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="MinorAdded"
        component={MinorAddedScreen}
        options={stepHeader({
          title: "Minor's Basic Info",
          totalSteps,
          position: 1,
        })}
        initialParams={{ store, nextScreen: "ExistingVisit" }}
      />
      <Stack.Screen
        name="ExistingVisit"
        component={ExistingVisitScreen}
        options={stepHeader({
          title: "Visit Details",
          totalSteps,
          position: 1,
        })}
        initialParams={{ store, visit }}
      />
      <Stack.Screen
        name="IdVerification"
        component={IdVerificationScreen}
        options={stepHeader({
          title: "Your Info",
          totalSteps,
          position: 1,
        })}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="PersonalInformationInput"
        component={PersonalInformationInputScreen}
        options={stepHeader({
          title: "Your Info",
          totalSteps,
          position: 1,
        })}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="PhoneNumberInput"
        component={PhoneNumberInputScreen}
        options={stepHeader({
          title: "Your Info",
          totalSteps,
          position: 1,
        })}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="NewServiceForVisit"
        component={NewServiceForVisitScreen}
        options={stepHeader({
          title: "Visit Details",
          totalSteps,
          position: 2,
        })}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="ServiceCategorySelectionForVisit"
        component={ServiceCategorySelectionForVisitScreen}
        options={stepHeader({
          title: "Visit Details",
          totalSteps,
          position: 2,
        })}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="ServiceSelectionForVisit"
        component={ServiceSelectionForVisitScreen}
        options={stepHeader({
          title: "Visit Details",
          totalSteps,
          position: 2,
        })}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="ServiceDetailsEntryForVisit"
        component={ServiceDetailsEntryForVisitScreen}
        options={stepHeader({
          title: "Visit Details",
          totalSteps,
          position: 2,
        })}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="ReviewServicesForVisit"
        component={ReviewServicesForVisitScreen}
        options={stepHeader({
          title: "Visit Details",
          totalSteps,
          position: 2,
        })}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="MedicalHistoryForVisit"
        component={MedicalHistoryForVisitScreen}
        options={stepHeader({
          title: "Health Info",
          totalSteps,
          position: 3,
        })}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="MedicalConcernsForVisit"
        component={MedicalConcernsForVisitScreen}
        options={stepHeader({
          title: "Health Info",
          totalSteps,
          position: 3,
        })}
        initialParams={{
          store,
          nextScreen: shouldFillConsent ? "ConsentQuestions" : "ReviewVisit",
        }}
      />
      <Stack.Screen
        name="ConsentQuestions"
        component={ConsentQuestions}
        options={stepHeader({
          title: "Consent",
          totalSteps,
          position: 4,
        })}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="ReviewVisit"
        component={ReviewVisitScreen}
        options={stepHeader({
          title: "Review",
          totalSteps,
          position: totalSteps + 1,
        })}
        initialParams={{ store, visit }}
      />
      <Stack.Screen
        name="SignatureCapture"
        options={stepHeader({
          title: "Signature",
          totalSteps,
          position: totalSteps + 1,
        })}
        initialParams={{ store, visit }}
      >
        {(props) => (
          <SignatureCapture {...props} isUnscheduled={!visit.scheduledFor} />
        )}
      </Stack.Screen>
      <Stack.Screen
        name="SignatureCaptureWeb"
        options={stepHeader({
          title: "Signature",
          totalSteps,
          position: totalSteps + 1,
        })}
        initialParams={{ store, visit }}
      >
        {(props) => (
          <SignatureCaptureWeb {...props} isUnscheduled={!visit.scheduledFor} />
        )}
      </Stack.Screen>
      <Stack.Screen
        name="VisitConfirmed"
        component={VisitConfirmedScreen}
        options={{ headerShown: false }}
        initialParams={{ store, shouldFillConsent }}
      />
    </Stack.Navigator>
  );
};

export default ExistingVisitFlow;
